import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Edit, Trash, Search, Filter, X, Calendar, DollarSign, User, FileText, CheckCircle, Clock, ArrowRight, Save, LayoutGrid, List, Upload, Image as ImageIcon } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const ThanhToanManagement = () => {
    // State Management
    const [paymentList, setPaymentList] = useState([]);
    const [customers, setCustomers] = useState([]); // DSKH

    // Data from 3 sources
    const [vanData, setVanData] = useState([]);
    const [dominoData, setDominoData] = useState([]);
    const [phuPhamData, setPhuPhamData] = useState([]);
    const [nhapBaoBiData, setNhapBaoBiData] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Current Payment State
    const [currentPayment, setCurrentPayment] = useState({
        'ID_TT': '',
        'MA_KH': '',
        'TEN_KHACHHANG': '',
        'SOPHIEU': '', // Selected Voucher
        'TONGTIEN': 0, // Original Voucher Amount
        'NGAY_THANHTOAN': new Date().toISOString().split('T')[0],
        'LOAI_THANHTOAN': 'THU', // THU (Khách trả) or CHI (Trả NCC)
        'SOTIEN_THANHTOAN': 0,
        'NGUOIPHUTRACH': '',
        'HINH_ANH': '',
        'GHICHU': ''
    });
    const [selectedImage, setSelectedImage] = useState(null);

    // Filtering & Pagination
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // NEW
    const [sortConfig, setSortConfig] = useState({ key: 'NGAY_THANHTOAN', direction: 'desc' }); // NEW
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Voucher Selection Modal State
    const [showVoucherSelect, setShowVoucherSelect] = useState(false);
    const [selectedCustomerVouchers, setSelectedCustomerVouchers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

    // Stats
    const stats = useMemo(() => {
        let totalThu = 0;
        let totalChi = 0;
        let totalHoan = 0;

        paymentList.forEach(p => {
            const val = parseFloat(p.SOTIEN_THANHTOAN) || 0;
            if (p.LOAI_THANHTOAN === 'THU') totalThu += val;
            else if (p.LOAI_THANHTOAN === 'CHI') totalChi += val;
            else if (p.LOAI_THANHTOAN === 'HOAN_TIEN') totalHoan += val;
        });

        return { totalThu, totalChi, totalHoan };
    }, [paymentList]);

    // ================= INITIALIZATION =================
    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setIsLoading(true);
        try {
            // 1. Fetch DSKH for mapping
            const dskhRes = await authUtils.apiRequestKHO('DSKH', 'Find', {});
            setCustomers(Array.isArray(dskhRes) ? dskhRes : []);

            // 2. Fetch Payments History
            const paymentRes = await authUtils.apiRequestKHO('THANH_TOAN', 'Find', {});
            setPaymentList(Array.isArray(paymentRes) ? paymentRes : []);

            // 3. Fetch Source Data (Ván, Domino, Phụ phẩm)
            // Note: Adjust API names based on actual usage in other files
            const vanRes = await authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {});
            setVanData(Array.isArray(vanRes) ? vanRes : []);

            const dominoRes = await authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Find', {});
            setDominoData(Array.isArray(dominoRes) ? dominoRes : []);

            const phuPhamRes = await authUtils.apiRequest('XUAT_PHUPHAM', 'Find', {});
            setPhuPhamData(Array.isArray(phuPhamRes) ? phuPhamRes : []);

            const baoBiRes = await authUtils.apiRequest('NHAPBAOBI', 'Find', {});
            setNhapBaoBiData(Array.isArray(baoBiRes) ? baoBiRes : []);

        } catch (error) {
            console.error("Error fetching data:", error);
            // toast.error("Lỗi tải dữ liệu: " + error.message); 
            // Suppress error toast to avoid spam on init if tables don't exist yet
        } finally {
            setIsLoading(false);
        }
    };

    // ================= HELPERS =================
    const formatCurrency = (value) => {
        if (!value && value !== 0) return '0 ₫';
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('vi-VN');
        } catch { return dateString; }
    };

    // ================= LOGIC HANDLERS =================

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload
        const toastId = toast.loading("Đang tải ảnh lên...");
        try {
            const result = await authUtils.uploadImage(file);
            if (result && result.success && result.url) {
                setCurrentPayment(prev => ({ ...prev, 'HINH_ANH': result.url }));
                toast.update(toastId, { render: "Tải ảnh thành công", type: "success", isLoading: false, autoClose: 2000 });
            } else {
                throw new Error("Không nhận được URL ảnh");
            }
        } catch (error) {
            console.error(error);
            toast.update(toastId, { render: "Lỗi tải ảnh: " + error.message, type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setCurrentPayment(prev => ({ ...prev, 'HINH_ANH': '' }));
    };

    const handleOpenModal = () => {
        // Reset form
        const user = authUtils.getUserData();
        setCurrentPayment({
            'ID_TT': `TT-${Date.now()}`,
            'MA_KH': '',
            'TEN_KHACHHANG': '',
            'SOPHIEU': '',
            'TONGTIEN': 0,
            'NGAY_THANHTOAN': new Date().toISOString().split('T')[0],
            'LOAI_THANHTOAN': 'THU',
            'SOTIEN_THANHTOAN': 0,
            'NGUOIPHUTRACH': user ? (user['Họ và Tên'] || user.username) : '',
            'HINH_ANH': '',
            'GHICHU': ''
        });
        setSelectedImage(null);
        setCustomerSearch('');
        setShowModal(true);
        setShowVoucherSelect(false);
    };

    const customerDropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCustomerSelect = (customer) => {
        let type = 'THU';
        if (customer.PHAN_LOAI === 'NCC') type = 'CHI';
        else if (customer.PHAN_LOAI === 'KH') type = 'THU';

        setCurrentPayment(prev => ({
            ...prev,
            'MA_KH': customer.MA_KH,
            'TEN_KHACHHANG': customer.TEN_KHACHHANG,
            'LOAI_THANHTOAN': type, // Auto-set based on PHAN_LOAI
            // Reset selected voucher when customer changes
            'SOPHIEU': '',
            'TONGTIEN': 0,
            'SOTIEN_THANHTOAN': 0
        }));
        setCustomerSearch(customer.TEN_KHACHHANG);
        setShowCustomerDropdown(false);

        // Prepare voucher list for this customer
        prepareVouchersForCustomer(customer.TEN_KHACHHANG);
    };

    const prepareVouchersForCustomer = (customerName) => {
        if (!customerName) return;
        const normalize = (str) => str?.trim().toLowerCase();
        const target = normalize(customerName);

        // Normalize source data into common structure
        // Structure: { source, sophieu, ngay, tongtien, dathanhtoan, conlai }

        // 1. Ván
        const vanVouchers = vanData.filter(v => normalize(v.NCC_KHACHHANG) === target).map(v => ({
            source: 'Thành phẩm',
            sophieu: v.SOPHIEU,
            ngay: v.NGAYNHAP_XUAT,
            tongtien: parseFloat(v.TONGTIEN) || 0,
            type: v.NGHIEP_VU // NHAP/XUAT
        }));

        // 2. Domino
        const dominoVouchers = dominoData.filter(v => normalize(v.NCC_KHACHHANG) === target).map(v => ({
            source: 'Domino',
            sophieu: v.SOPHIEU,
            ngay: v.NGAYNHAP_XUAT,
            tongtien: parseFloat(v.TONGTIEN) || 0,
            type: v.NGHIEP_VU // NHAP/XUAT
        }));

        // 3. Phụ phẩm (Only XUAT expected)
        const phuPhamVouchers = phuPhamData.filter(v => normalize(v.NCC_KHACHHANG) === target).map(v => ({
            source: 'Phụ phẩm',
            sophieu: v.SOPHIEU,
            ngay: v.NGAY_XUAT,
            tongtien: parseFloat(v.TONGTIEN) || 0,
            type: 'XUAT' // Always XUAT
        }));

        // 4. Nhập Bao Bì (Only NHAP expected)
        const baoBiVouchers = nhapBaoBiData.filter(v => normalize(v.NCC) === target).map(v => ({
            source: 'Nhập Bao Bì',
            sophieu: v['SỐ XE'] || v['SỐ LÔ'] || `BB-${v.ID}`, // Use SỐ XE as preferred identifier
            // Date field might vary, try common names
            ngay: v['NGÀY'] || v.NGAY || v.DATE || new Date().toISOString(),
            tongtien: 0, // No amount in default data
            type: 'NHAP'
        }));

        const allVouchers = [...vanVouchers, ...dominoVouchers, ...phuPhamVouchers, ...baoBiVouchers];

        // Calculate Paid Amount for each voucher from paymentList
        const vouchersWithBalance = allVouchers.map(v => {
            // Find all payments for this SOPHIEU
            const paid = paymentList
                .filter(p => p.SOPHIEU === v.sophieu)
                .reduce((sum, p) => {
                    const val = parseFloat(p.SOTIEN_THANHTOAN) || 0;
                    if (p.LOAI_THANHTOAN === 'HOAN_TIEN') {
                        // If Hoan Tien, it means reversing a payment, so it subtracts from paid amount
                        return sum - val;
                    }
                    // Standard THU/CHI adds to paid amount
                    return sum + val;
                }, 0);

            return {
                ...v,
                dathanhtoan: paid,
                conlai: v.tongtien - paid
            };
        }).filter(v => Math.abs(v.conlai) > 1000); // Only show vouchers with balance > 1000 VND

        // Sort by date desc
        vouchersWithBalance.sort((a, b) => new Date(b.ngay) - new Date(a.ngay));

        setSelectedCustomerVouchers(vouchersWithBalance);
        // Automatically show voucher selection list if customer selected
        if (vouchersWithBalance.length > 0) setShowVoucherSelect(true);
        else toast.info("Khách hàng này hiện không có phiếu công nợ nào!");
    };

    const handleVoucherSelect = (voucher) => {
        setCurrentPayment(prev => ({
            ...prev,
            'SOPHIEU': voucher.sophieu,
            'TONGTIEN': voucher.tongtien,
            'SOTIEN_THANHTOAN': voucher.conlai, // Default to pay full remaining
            // Auto detect Payment Type
            'LOAI_THANHTOAN': (voucher.type === 'XUAT') ? 'THU' : 'CHI',
            'GHICHU': `Thanh toán cho phiếu ${voucher.source} ${voucher.sophieu}`
        }));
        // setShowVoucherSelect(false);
    };

    const handleEditRecord = (item) => {
        setCurrentPayment({
            ...item,
            // Ensure dates are formatted for input
            'NGAY_THANHTOAN': new Date(item.NGAY_THANHTOAN).toISOString().split('T')[0]
        });
        setSelectedImage(item.HINH_ANH || null);
        setCustomerSearch(item.TEN_KHACHHANG || '');
        // Re-fetch vouchers for this customer to populate the list if needed, though arguably we might not want to re-select voucher if it's already saved. 
        // But for consistency let's load them.
        prepareVouchersForCustomer(item.TEN_KHACHHANG);

        setShowModal(true);
    };

    const handleDeleteRecord = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa phiếu thanh toán này?")) {
            try {
                await authUtils.apiRequestKHO('THANH_TOAN', 'Delete', { "ID_TT": id });
                toast.success("Xóa thành công!");
                fetchAllData();
            } catch (error) {
                console.error(error);
                toast.error("Lỗi khi xóa: " + error.message);
            }
        }
    };

    const handleSave = async () => {
        if (!currentPayment.MA_KH || !currentPayment.SOPHIEU) {
            toast.error("Vui lòng chọn khách hàng và phiếu cần thanh toán");
            return;
        }
        if (!currentPayment.SOTIEN_THANHTOAN || currentPayment.SOTIEN_THANHTOAN <= 0) {
            toast.error("Số tiền thanh toán phải lớn hơn 0");
            return;
        }

        setIsSubmitting(true);
        try {
            // Check if ID_TT exists in current list to decide generic Add vs Update (if backend supports Update)
            // Or simply use 'Add' with overwrite if the backend supports it. 
            // Most of these sheets based backends use "Update" action.

            const isEdit = paymentList.some(p => p.ID_TT === currentPayment.ID_TT);
            const action = isEdit ? 'Update' : 'Add';
            // For Update, usually we need Keys. Assuming ID_TT is key.

            // Note: If 'Update' is not supported, we might need 'Delete' then 'Add' or specific logic.
            // Assuming 'Update' works for now based on other files.

            // Prepare payload
            // Some backends require specifc structure.
            const payload = isEdit ?
                { "Keys": { "ID_TT": currentPayment.ID_TT }, "Values": currentPayment } :
                { "Rows": [currentPayment] };

            await authUtils.apiRequestKHO('THANH_TOAN', action, payload);

            toast.success(isEdit ? "Cập nhật thành công!" : "Lưu thanh toán thành công!");
            setShowModal(false);
            fetchAllData();

        } catch (error) {
            console.error(error);
            toast.error("Lỗi khi lưu: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Sorting Helper
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Filter List
    const filteredPayments = useMemo(() => {
        let data = paymentList.filter(item => {
            const matchSearch =
                item.TEN_KHACHHANG?.toLowerCase().includes(search.toLowerCase()) ||
                item.SOPHIEU?.toLowerCase().includes(search.toLowerCase()) ||
                item.ID_TT?.toLowerCase().includes(search.toLowerCase());

            // Date filter
            let matchDate = true;
            if (dateFrom && dateTo) {
                const d = new Date(item.NGAY_THANHTOAN);
                matchDate = d >= new Date(dateFrom) && d <= new Date(dateTo);
            }

            // Status Filter
            const matchStatus = statusFilter === 'ALL' || item.LOAI_THANHTOAN === statusFilter;

            return matchSearch && matchDate && matchStatus;
        });

        // Sorting
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle numbers
                if (sortConfig.key === 'SOTIEN_THANHTOAN') {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                }

                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return data;
    }, [paymentList, search, dateFrom, dateTo, statusFilter, sortConfig]);

    // Pagination
    const pageCount = Math.ceil(filteredPayments.length / itemsPerPage);
    const paginatedItems = filteredPayments.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


    return (
        <div className="p-4 bg-gray-50 min-h-screen">
            <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-indigo-100">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-200 text-white">
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">Quản Lý Thanh Toán</h1>
                                <p className="text-sm text-gray-500">Theo dõi Thu/Chi và Công Nợ tổng hợp</p>
                            </div>
                        </div>
                        <button
                            onClick={handleOpenModal}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-md transition-all"
                        >
                            <Plus className="w-5 h-5" /> Tạo Thanh Toán
                        </button>
                    </div>

                    {/* Stats Cards - Filterable */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div
                            onClick={() => setStatusFilter(statusFilter === 'THU' ? 'ALL' : 'THU')}
                            className={`rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-[1.02] ${statusFilter === 'THU' ? 'ring-4 ring-offset-2 ring-emerald-400' : 'opacity-90 hover:opacity-100'
                                } bg-gradient-to-r from-emerald-500 to-teal-600`}
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-emerald-100 text-sm font-medium">Tổng Phiếu Thu</p>
                                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(stats.totalThu)}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><ArrowRight className="w-6 h-6 rotate-45" /></div>
                            </div>
                        </div>

                        <div
                            onClick={() => setStatusFilter(statusFilter === 'CHI' ? 'ALL' : 'CHI')}
                            className={`rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-[1.02] ${statusFilter === 'CHI' ? 'ring-4 ring-offset-2 ring-rose-400' : 'opacity-90 hover:opacity-100'
                                } bg-gradient-to-r from-rose-500 to-pink-600`}
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-rose-100 text-sm font-medium">Tổng Phiếu Chi</p>
                                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(stats.totalChi)}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><ArrowRight className="w-6 h-6 -rotate-45" /></div>
                            </div>
                        </div>

                        <div
                            onClick={() => setStatusFilter(statusFilter === 'HOAN_TIEN' ? 'ALL' : 'HOAN_TIEN')}
                            className={`rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-[1.02] ${statusFilter === 'HOAN_TIEN' ? 'ring-4 ring-offset-2 ring-blue-400' : 'opacity-90 hover:opacity-100'
                                } bg-gradient-to-r from-blue-500 to-indigo-600`}
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">Tổng Hoàn Tiền</p>
                                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(stats.totalHoan)}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg"><Clock className="w-6 h-6" /></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex-1 min-w-[200px] relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Tìm theo Khách hàng, Số phiếu..."
                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Từ:</span>
                        <input
                            type="date"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Đến:</span>
                        <input
                            type="date"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200 select-none">
                                <tr>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('NGAY_THANHTOAN')}
                                    >
                                        Ngày {sortConfig.key === 'NGAY_THANHTOAN' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('TEN_KHACHHANG')}
                                    >
                                        Khách Hàng / NCC {sortConfig.key === 'TEN_KHACHHANG' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('SOPHIEU')}
                                    >
                                        Phiếu Gốc {sortConfig.key === 'SOPHIEU' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('SOTIEN_THANHTOAN')}
                                    >
                                        Số Tiền {sortConfig.key === 'SOTIEN_THANHTOAN' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('LOAI_THANHTOAN')}
                                    >
                                        Loại {sortConfig.key === 'LOAI_THANHTOAN' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Hình Ảnh</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ghi Chú</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paginatedItems.length > 0 ? (
                                    paginatedItems.map((item) => (
                                        <tr key={item.ID_TT} className="hover:bg-gray-50 transition-colors">
                                            {/* <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.ID_TT}</td> */}
                                            <td className="px-6 py-4 text-sm text-gray-600">{formatDate(item.NGAY_THANHTOAN)}</td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{item.TEN_KHACHHANG}</div>
                                                <div className="text-xs text-gray-500">{item.MA_KH}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded inline-block">
                                                    {item.SOPHIEU}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                                                {formatCurrency(item.SOTIEN_THANHTOAN)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${item.LOAI_THANHTOAN === 'THU'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : item.LOAI_THANHTOAN === 'CHI' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {item.LOAI_THANHTOAN === 'HOAN_TIEN' ? 'Hoàn' : item.LOAI_THANHTOAN}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {item.HINH_ANH ? (
                                                    <a href={item.HINH_ANH} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">
                                                        <ImageIcon className="w-5 h-5 mx-auto" />
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.GHICHU}>
                                                {item.GHICHU}
                                            </td>
                                            <td className="px-6 py-4 text-right space-x-2">
                                                <button
                                                    onClick={() => handleEditRecord(item)}
                                                    className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                                    title="Chỉnh sửa"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteRecord(item.ID_TT)}
                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                    title="Xóa"
                                                >
                                                    <Trash className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-10 text-center text-gray-500">
                                            Chưa có dữ liệu thanh toán nào.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    {pageCount > 1 && (
                        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50">
                            <span className="text-sm text-gray-600">
                                Trang {currentPage} / {pageCount}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded hover:bg-white disabled:opacity-50"
                                >
                                    Trước
                                </button>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
                                    disabled={currentPage === pageCount}
                                    className="px-3 py-1 border border-gray-300 rounded hover:bg-white disabled:opacity-50"
                                >
                                    Sau
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Tạo Thanh Toán */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <DollarSign className="w-6 h-6 text-indigo-600" />
                                Tạo Phiếu Thanh Toán Mới
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Left Col: Info */}
                            <div className="space-y-5">
                                <div className="grid grid-cols-1 gap-4">

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Ngày Thanh Toán</label>
                                        <input
                                            type="date"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={currentPayment.NGAY_THANHTOAN}
                                            onChange={(e) => setCurrentPayment({ ...currentPayment, NGAY_THANHTOAN: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Khách Hàng / Nhà Cung Cấp <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                            placeholder="Tìm tên khách hàng..."
                                            value={customerSearch}
                                            onChange={(e) => {
                                                setCustomerSearch(e.target.value);
                                                setShowCustomerDropdown(true);
                                            }}
                                            onFocus={() => setShowCustomerDropdown(true)}
                                        />
                                    </div>
                                    {showCustomerDropdown && (
                                        <div ref={customerDropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {customers
                                                .filter(c => c.TEN_KHACHHANG?.toLowerCase().includes(customerSearch.toLowerCase()))
                                                .map((c, i) => (
                                                    <div
                                                        key={i}
                                                        className="px-4 py-3 hover:bg-indigo-50 cursor-pointer border-b border-gray-50 last:border-0"
                                                        onClick={() => handleCustomerSelect(c)}
                                                    >
                                                        <div className="font-medium text-gray-800">{c.TEN_KHACHHANG}</div>
                                                        <div className="text-xs text-gray-500">{c.MA_KH}</div>
                                                    </div>
                                                ))}
                                            {customers.length === 0 && (
                                                <div className="px-4 py-3 text-gray-500 text-sm">Đang tải danh sách...</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Loại Phiếu</label>
                                        <select
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={currentPayment.LOAI_THANHTOAN}
                                            onChange={e => setCurrentPayment({ ...currentPayment, LOAI_THANHTOAN: e.target.value })}
                                        >
                                            <option value="THU">Phiếu Thu</option>
                                            <option value="CHI">Phiếu Chi</option>
                                            <option value="HOAN_TIEN">Hoàn tiền</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Người Phụ Trách</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg"
                                            value={currentPayment.NGUOIPHUTRACH}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>

                                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-indigo-700 font-medium">Phiếu Gốc:</span>
                                        <span className="font-bold text-gray-900">{currentPayment.SOPHIEU || 'Chưa chọn'}</span>
                                    </div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-indigo-700 font-medium">Tổng Giá Trị:</span>
                                        <span className="font-bold text-gray-900">{formatCurrency(currentPayment.TONGTIEN)}</span>
                                    </div>
                                    <div className="my-2 border-t border-indigo-200"></div>
                                    <div className="flex items-center justify-between">
                                        <label className="block text-sm font-bold text-gray-800">Số Tiền Thanh Toán <span className="text-red-500">*</span></label>
                                        <div className="relative w-1/2">
                                            <input
                                                type="number"
                                                className="w-full px-4 py-2 border-2 border-indigo-300 rounded-lg focus:border-indigo-600 focus:ring-0 outline-none font-bold text-right text-indigo-700"
                                                value={currentPayment.SOTIEN_THANHTOAN}
                                                onChange={e => setCurrentPayment({ ...currentPayment, SOTIEN_THANHTOAN: parseFloat(e.target.value) || 0 })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Ghi Chú</label>
                                    <textarea
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none h-24 resize-none"
                                        placeholder="Nhập ghi chú (nếu có)..."
                                        value={currentPayment.GHICHU}
                                        onChange={e => setCurrentPayment({ ...currentPayment, GHICHU: e.target.value })}
                                    ></textarea>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Hình Ảnh Minh Chứng</label>
                                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-500 transition-colors relative bg-white">
                                        <div className="space-y-1 text-center">
                                            {selectedImage ? (
                                                <div className="relative inline-block">
                                                    <img src={selectedImage} alt="Preview" className="max-h-48 rounded-lg shadow-md" />
                                                    <button
                                                        onClick={handleRemoveImage}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md transform transition-transform hover:scale-110"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <ImageIcon className="mx-auto h-12 w-12 text-gray-300" />
                                                    <div className="flex text-sm text-gray-600 justify-center items-center mt-2">
                                                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                            <span>Tải ảnh lên</span>
                                                            <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleImageChange} accept="image/*" />
                                                        </label>
                                                        <p className="pl-1">hoặc kéo thả</p>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF tối đa 10MB</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Col: Voucher Selection */}
                            <div className="bg-gray-50 rounded-xl border border-gray-200 flex flex-col h-full overflow-hidden">
                                <div className="p-4 border-b border-gray-200 bg-white">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <List className="w-5 h-5 text-indigo-600" />
                                        Chọn Phiếu Cần Thanh Toán
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-1">Danh sách các phiếu chưa thanh toán hết của khách hàng</p>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                    {!currentPayment.TEN_KHACHHANG ? (
                                        <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                                            <User className="w-12 h-12 mb-2 opacity-20" />
                                            <p>Vui lòng chọn khách hàng trước</p>
                                        </div>
                                    ) : selectedCustomerVouchers.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                                            <CheckCircle className="w-12 h-12 mb-2 text-green-500 opacity-50" />
                                            <p>Khách hàng này không có công nợ nào!</p>
                                        </div>
                                    ) : (
                                        selectedCustomerVouchers.map((v, i) => (
                                            <div
                                                key={i}
                                                onClick={() => handleVoucherSelect(v)}
                                                className={`p-3 rounded-xl border cursor-pointer transition-all ${currentPayment.SOPHIEU === v.sophieu
                                                    ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500 shadow-md'
                                                    : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-sm'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 mr-2">{v.source}</span>
                                                        <span className="font-bold text-gray-800 text-sm">{v.sophieu}</span>
                                                    </div>
                                                    <span className="text-xs text-gray-500">{formatDate(v.ngay)}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-600">Tổng: {formatCurrency(v.tongtien)}</span>
                                                    <span className="text-gray-500 line-through text-xs">{formatCurrency(v.dathanhtoan)}</span>
                                                </div>
                                                <div className="mt-2 pt-2 border-t border-dashed border-gray-200 flex justify-between items-center">
                                                    <span className="text-xs font-semibold text-gray-500 uppercase">Còn lại</span>
                                                    <span className="font-bold text-red-600">{formatCurrency(v.conlai)}</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors"
                            >
                                Hủy Bỏ
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 flex items-center gap-2 disabled:opacity-70"
                            >
                                {isSubmitting ? 'Đang Lưu...' : (
                                    <>
                                        <Save className="w-5 h-5" /> Lưu Thanh Toán
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThanhToanManagement;
